/**
 * @(#)2019/7/24.
 * 请求包装
 * @author 足道
 * Copyright (c) 2019, YUNXI. All rights reserved.
 * YUNXI PROPRIETARY/CONFIDENTIAL. Use is subject to license terms.
 */

import { message } from "antd";
import React from "react";
import request from "utils/request";
import { cloneDeep, isObject, isArray, isString } from "lodash";
import {
  factory,
  get,
  strFormat,
  filterEmpty,
  deepArrayToStr,
  localFactory,
  merge,
  paramFactory,
  getQueryString,
} from "./index";
import config from "../config";

/**
 * 空函数
 *
 */
const emptyFun = () => {};

/**
 * url加工函数
 *
 * @param {*} param
 * @param {*} data
 * @returns
 */
const urlProcess = function (param, data) {
  return strFormat(param.url, data, (key) => delete data[key]);
};

/**
 * 工厂选择器
 *
 * @param {*} data
 * @param {*} param
 * @returns
 */
const factorySelector = function (data, param) {
  let result = {};
  const beforeRequest = get(param, "beforeRequest", null);
  const requestModel = get(param, "requestModel", null);
  if (requestModel instanceof Array) {
    result = localFactory(data, requestModel, beforeRequest);
  } else if (typeof requestModel === "string") {
    result = get(data, requestModel, {});
  } else {
    result = factory(data, requestModel, beforeRequest);
  }
  return result;
};

/**
 * 请求数据源data加工
 *
 * @param {*} data
 * @param {*} param
 */
const dataProcess = function (data, param) {
  data = factorySelector(data, param);
  if (param.filterEmpty) {
    data = filterEmpty(data, param.filterEmpty);
  }
  // 深度连接字符串
  const method = get(param, "method", "get");
  const isSubmit = ["get", "delete"].includes(method.toLowerCase());
  if (isSubmit && param.joinArray === true) {
    const joinMark = get(param, "joinMark", ",");
    data = deepArrayToStr(data, joinMark);
  }
  return data;
};

/**
 * 请求参数加工函数
 *
 * @param {object} requestParam
 * @returns
 */
const requestProcess = function (requestParam) {
  const param = cloneDeep(requestParam);
  let data = get(param, "data", {});
  if (Object.keys(data).length !== 0) {
    param.data = dataProcess(data, param);
  }
  param.url = urlProcess(param, data);
  return param;
};

/**
 * 响应加工函数
 *
 * @param {object} param
 * @param {object} data
 * @returns
 */
const resProcess = function (param, data) {
  const responseModel = get(param, "responseModel", null);
  const afterRequest = get(param, "afterRequest", null);
  const dataByRes = factory(data, responseModel, afterRequest);
  return dataByRes;
};

/**
 * 接口数据选择器，自定义还是组件接口请求
 *
 * @param {*} requestParam
 * @returns
 */
const beforeResponse = async function (requestParam) {
  let response = null,
    param = {};
  const intercept = get(requestParam, "intercept", null);
  if (intercept instanceof Function) {
    response = await intercept(requestParam);
  } else {
    param = requestProcess(requestParam);
    response = await request(param);
  }
  return { response, param };
};

/**
 * 消息处理函数
 *
 * @param {*} requestParam 请求参数
 * @param {*} response 接口响应
 * @param {*} resultMsg 错误信息
 */
const requestMessage = function (requestParam, response, resultMsg) {
  const msg = get(requestParam, "message", resultMsg);
  if (msg instanceof Function) {
    msg(requestParam, response);
  } else if (typeof msg === "string" || typeof msg === "number") {
    message.error(msg + "");
  } else if (msg === true) {
    message.error(resultMsg);
  }
};

/**
 * description: 解析参数指令
 * @param {obj} request 参数
 * return {*} 解析指令后参数
 */
const commandFactory = function (param = {}) {
  const copyParm = { ...param };
  const url = get(param, "url", "");
  const data = get(param, "data", {});
  if (copyParm.yxCommand) {
    copyParm.url = paramFactory(url);
    copyParm.data = paramFactory(data);
  }
  return copyParm;
};

/**
 * 组件内部请求函数，依赖外部 request
 * request原本的参数
 * @param {Function||null} intercept  当此参数存在时，请求接口将被外部控制，需要返回一个与接口结构一致的值来
 * @param {string} url  接口请求的地址，不需要传host以及前缀信息，在脚手架中会重新组装
 * @param {string} searchPath  当指定 searchPath 时，将会把查询面板的表单参数放入此字段请求  比如searchPath="filter"
 * @param {object} data  接口请求的参数，默认值是当前表单或分组表单的所有控件信息，该参数为控件信息之外的额外信息
 * @param {string} method  post | get | put | detele 接口请求的类型
 * @param {string} yxCommand {boole} 开启指令模式，会对所有参数中指令进行匹配
 * @param {function(response,originalResponse,requestData)} callback  接口请求成功后的回调，  会返回接口经过responseModel映射的的response，未经过模型映射的originalResponse，和请求的data，该函数是请求的最后一个步骤
 * @param {object} headers  请求头
 * @param {boolean|object} filterEmpty 是否过滤request的data中的无效值，比如undefined, null, '', []等
 * @param filterEmpty:{ mode: 'deep', handle: (key, value) => { return boolean }},filterEmpty开启后，函数默认会过滤 undefined, null, '', [] 这些值，如果有特殊的字段需要保留，则传入handle函数 并且返回true
 *
 * 本函数新增的额外参数，仅对该函数起效，不影响外部request本身
 * @param {function(requestData){return requestData}} beforeRequest 请求开始前，data的处理函数，在映射完模型后执行
 * @param {object | array} requestModel 请求参数的数据模型，最终的请求数据以该模型映射后的结果为准
 *         可选择object绝对映射，：{age:'list.age',ids:'id'} 除了age和id字段外，其它字段将会被删掉
 *         可选择array相对映射，：[{target:'list.age',source:'age',activeTarget: '动态key值'},{target:'list.address',source:'address',activeTarget: '动态key值'}] 除了age和id字段外，其它字段都不会被修改
 * @param {function(response){return response}} afterRequest 请求完成后，response的处理函数，在映射完模型后执行
 * @param {object} responseModel response数据模型，参考requestModel
 * @param {boolean} joinArray 为true时将会自动将类型为数组的值用 joinMark（默认为英文逗号,） 字段连接起来变成字符串，比如a=[1,2,3] 变成  a=1,2,3
 * @param {boolean} failure 接口调用失败回调
 * @param {boolean} message 消息处理逻辑，为true时，组件默认提示接口返回的值，false不使用组件内置的消息，可传入函数
 * @returns
 */
const widgetRequest = async function (requestParam) {
  const requestParam_ = merge(
    { ...config.request },
    commandFactory(requestParam)
  );
  let { response, param } = await beforeResponse(requestParam_);
  const { data, resultCode, resultMsg } = response;
  if (resultCode + "" == 0 + "") {
    const dataByRes = resProcess(param, data);
    const originalResponse = cloneDeep(response);
    response.data = dataByRes;
    const callback = get(param, "callback", emptyFun);
    callback(response, originalResponse, param.data);
    return response;
  } else {
    const failure = get(param, "failure", emptyFun);
    if (failure(response) !== false) {
      requestMessage(requestParam_, response, resultMsg);
      throw {
        resultCode,
        resultMsg,
      };
    }
  }
};

export default widgetRequest;



// WEBPACK FOOTER //
// ./src/yx-widget/utils/request.js